/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: inside a program, for the people who run it. Who it's for sets
 * the tabs (a gym program has Members; 1-to-1 (PT) does not), how it runs sets
 * the planner (daily calendar, week-by-week, or a set of workouts).
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@lemonadetraining/web-ui";
import { notifyActions, NOTIFICATION_TYPE, updateProduct } from "@lemonadetraining/web-shared";
import * as PRODUCTS from "@lemonadetraining/web-shared/constants/ProductConst";
import { LINE_TAB_TRIGGER, LINE_TABS_LIST } from "../../lib/lineTabs";
import { useUserAccess } from "../../hooks/useUserAccess";
import { loadStaffDirectory } from "../Box/People/staffDirectory";
import { ProgramHeader } from "./ProgramHeader";
import { DailyPlanner } from "./planner/DailyPlanner";
import { WeekPlanner } from "./planner/WeekPlanner";
import { ListPlanner } from "./planner/ListPlanner";
import type { PlannerRequest } from "./planner/plannerProps";
import ProgramMembersTab from "./ProgramMembersTab";
import ProgramSettingsTab from "./ProgramSettingsTab";
import { runsAs, type ProgramDoc } from "./programTypes";

export interface ProgramPageProps {
  program: ProgramDoc;
  canManage: boolean;
  isCreator: boolean;
}

type Tab = "plan" | "members" | "settings";

export default function ProgramPage({ program: initial, canManage, isCreator }: ProgramPageProps) {
  const dispatch = useDispatch();
  const access = useUserAccess();
  const [program, setProgram] = useState<ProgramDoc>(initial);
  const [params, setParams] = useSearchParams();
  const [request, setRequest] = useState<PlannerRequest | null>(null);
  const [publicBusy, setPublicBusy] = useState(false);
  const [creatorName, setCreatorName] = useState<string | undefined>(undefined);
  const runs = runsAs(program);
  const showMembers = !program.isPT;

  const requested = params.get("tab");
  const tab: Tab =
    requested === "settings" || (requested === "members" && showMembers) ? requested : "plan";
  const goTo = (next: Tab) => {
    const p = new URLSearchParams(params);
    if (next === "plan") p.delete("tab");
    else p.set("tab", next);
    setParams(p, { replace: true });
  };

  // A gym admin sees who made a colleague's program; a creator knows it is theirs.
  useEffect(() => {
    if (isCreator || !program.boxID || !access.isBoxAdmin || !program.ownerUID) return undefined;
    let live = true;
    loadStaffDirectory(program.boxID)
      .then((directory) => {
        const name = program.ownerUID ? directory.names[program.ownerUID] : undefined;
        if (live && name) setCreatorName(name);
      })
      .catch((error: unknown) => console.error("Error loading coach names:", error));
    return () => {
      live = false;
    };
  }, [isCreator, program.boxID, program.ownerUID, access.isBoxAdmin]);

  const togglePublic = async () => {
    setPublicBusy(true);
    const next = !program.landingPage;
    try {
      await updateProduct(program.programmingUID, PRODUCTS.TYPE.Programming, { landingPage: next });
      setProgram((prev) => ({ ...prev, landingPage: next }));
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.success,
          title: next ? "Now on your public page" : "Hidden from your public page",
          message: "",
        })
      );
    } catch (error: unknown) {
      console.error("Error changing the public listing:", error);
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.error,
          title: "Couldn't change your public page",
          message: "Nothing changed. Try again.",
        })
      );
    } finally {
      setPublicBusy(false);
    }
  };

  const plannerProps = {
    program,
    canManage,
    canChangeWeeks: isCreator || access.isBoxAdmin,
    // Said plainly: who can read a private note on this program. A 1-to-1 (PT)
    // note is its coach's and the gym admins' only (the notes callables enforce it).
    noteLabel: !program.boxID
      ? "Just for you"
      : !program.isPT
        ? "For coaches only"
        : isCreator || access.isBoxAdmin
          ? "The coach and gym admins only"
          : undefined,
    request,
    onRequestHandled: () => setRequest(null),
    onWeeksChange: (weeks: number) => setProgram((prev) => ({ ...prev, weeks })),
  };

  return (
    <div className="flex w-full flex-col gap-6" data-testid="program-page">
      <ProgramHeader
        program={program}
        runs={runs}
        canManage={canManage}
        byline={creatorName ? `by ${creatorName}` : undefined}
        busy={publicBusy}
        onAddWorkout={() => {
          goTo("plan");
          setRequest("add");
        }}
        onCopyWeek={() => {
          goTo("plan");
          setRequest("copy");
        }}
        onTogglePublic={program.isPT || !program.active ? undefined : togglePublic}
        onOpenSettings={() => goTo("settings")}
      />
      {!canManage && (
        <Alert>
          <AlertDescription>
            You can look but not change this program. Its creator or your gym&apos;s owner can
            change it.
          </AlertDescription>
        </Alert>
      )}
      <Tabs value={tab} onValueChange={(v) => goTo(v as Tab)} className="gap-4">
        <div className="border-b">
          <TabsList variant="line" className={LINE_TABS_LIST} aria-label="Program">
            <TabsTrigger value="plan" className={LINE_TAB_TRIGGER} data-testid="program-tab-plan">
              Plan
            </TabsTrigger>
            {showMembers && (
              <TabsTrigger
                value="members"
                className={LINE_TAB_TRIGGER}
                data-testid="program-tab-members"
              >
                Members
                <span className="text-muted-foreground tabular-nums">
                  <span className="sr-only">, </span>
                  {program.subscribers?.length ?? 0}
                </span>
              </TabsTrigger>
            )}
            <TabsTrigger
              value="settings"
              className={LINE_TAB_TRIGGER}
              data-testid="program-tab-settings"
            >
              Settings
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="plan">
          {runs === "daily" ? (
            <DailyPlanner {...plannerProps} />
          ) : runs === "weeks" ? (
            <WeekPlanner {...plannerProps} />
          ) : (
            <ListPlanner {...plannerProps} />
          )}
        </TabsContent>
        {showMembers && (
          <TabsContent value="members">
            <ProgramMembersTab
              programmingUID={program.programmingUID}
              memberCount={program.subscribers?.length ?? 0}
            />
          </TabsContent>
        )}
        <TabsContent value="settings">
          <ProgramSettingsTab
            program={program}
            canManage={canManage}
            creatorName={creatorName}
            onChanged={(patch) => setProgram((prev) => ({ ...prev, ...patch }))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
